"use no memo";

/* eslint-disable complexity */
import {
  isCateringOrderFlow,
  isDineInOrderFlow,
  isOffPremisesOrderFlow,
  isSessionStorageAllowed,
} from '@local/utils';
import { getHqIdFromUrl, getRestaurantIdFromUrl } from 'helpers/url';

export const EXPERIENCE_TYPES = {
  catering: 'catering',
  dineIn: 'dine-in',
  order: 'order',
} as const;

export type ExperienceType =
  (typeof EXPERIENCE_TYPES)[keyof typeof EXPERIENCE_TYPES];

export const EXPERIENCE_TYPE_STORAGE_KEY = 'sessionExperienceType';

export const ROUTES = {
  account: {
    isHqRequired: false,
    path: '/account',
  },
  checkout: {
    isHqRequired: true,
    isRestaurantRequired: true,
    path: '/checkout',
  },
  confirmation: {
    isOrderIdRequired: true,
    path: '',
  },
  history: {
    path: '/history',
  },
  locations: {
    isHqRequired: true,
    path: '/locations',
  },
  membershipCheckout: {
    isHqRequired: true,
    isRestaurantRequired: true,
    path: '/membership/checkout',
  },
  membershipConfirmation: {
    isHqRequired: true,
    isRestaurantRequired: true,
    path: '/membership/confirmation',
  },
  membershipList: {
    isHqRequired: false,
    path: '/membership',
  },
  membershipPlans: {
    isHqRequired: true,
    isRestaurantRequired: true,
    path: '/membership',
  },
  menu: {
    isHqRequired: true,
    isRestaurantRequired: true,
    path: '',
  },
  support: {
    isHqRequired: true,
    path: '/support',
  },
  inquiry: {
    isHqRequired: true,
    isRestaurantRequired: true,
    path: '/inquiry',
  },
};

export function getExperienceFromUrl(): ExperienceType | undefined {
  if (isOffPremisesOrderFlow()) return EXPERIENCE_TYPES.order;
  if (isCateringOrderFlow()) return EXPERIENCE_TYPES.catering;
  if (isDineInOrderFlow()) return EXPERIENCE_TYPES.dineIn;
  return undefined;
}

export function getExperienceType(): ExperienceType {
  // check url to determine experience type first
  const urlExperience = getExperienceFromUrl();
  if (urlExperience) return urlExperience;

  // if no experience type in url, check session storage
  if (typeof window !== 'undefined' && isSessionStorageAllowed()) {
    const saved =
      sessionStorage.getItem(EXPERIENCE_TYPE_STORAGE_KEY) || undefined;
    if (
      saved === EXPERIENCE_TYPES.catering ||
      saved === EXPERIENCE_TYPES.dineIn ||
      saved === EXPERIENCE_TYPES.order
    ) {
      return saved;
    }
  }

  // default to order experience
  return EXPERIENCE_TYPES.order;
}

export function buildRoute({
  experience,
  hqId,
  restaurantId,
  route,
  orderId,
  query,
}: {
  experience?: ExperienceType;
  hqId?: string;
  restaurantId?: string;
  route: {
    isHqRequired?: boolean;
    isRestaurantRequired?: boolean;
    isOrderIdRequired?: boolean;
    path: string;
  };
  orderId?: string;
  query?: { name: string; value: string };
}): string {
  const { isHqRequired, isRestaurantRequired, isOrderIdRequired, path } = route;
  const hasValidQuery = query && query.name && query.value;

  let url = '';

  const experienceType = experience || getExperienceType();

  // base segment
  url += `/${experienceType}`;

  // hq segment
  if (isHqRequired) url += `/${hqId}`;

  // location segment
  if (isRestaurantRequired) url += `/locations/${restaurantId}`;
  else if (isOrderIdRequired) url += `/history/${orderId}`;

  // path
  if (path) url += path;

  // query
  if (hasValidQuery) url += `?${query.name}=${query.value}`;

  return url;
}

/**
 * Creates a redirectURL to either a restaurant location
 * or Restaurant HQ
 */
export function getRestaurantRedirectUrl(): string {
  const hqId = getHqIdFromUrl();
  const restaurantId = getRestaurantIdFromUrl();
  return buildRoute({
    hqId,
    restaurantId,
    route: restaurantId ? ROUTES.menu : ROUTES.locations,
  });
}

export function buildAccountRoute({
  query,
}: {
  query?: { name: string; value: string };
}): string {
  const hasValidQuery = query && query.name && query.value;
  const queryParam = hasValidQuery ? `?${query.name}=${query.value}` : '';

  return `${ROUTES.account.path}${queryParam}`;
}

/**
 * builds a route to the menu page
 */
export function buildMenuRoute(): string {
  const hqId = getHqIdFromUrl();
  const restaurantId = getRestaurantIdFromUrl();
  return buildRoute({ hqId, restaurantId, route: ROUTES.menu });
}

/**
 * builds a route to the inquiry page
 */
export function buildInquiryRoute(): string {
  const hqId = getHqIdFromUrl();
  const restaurantId = getRestaurantIdFromUrl();
  return buildRoute({ hqId, restaurantId, route: ROUTES.inquiry });
}
